import React from "react";
import styled from "styled-components";
import { MainTemplate } from "../../templates/MainTemplate";
import { ImageUpload } from "../../atoms/ImageUpload";
import { Toast } from "../../atoms/Toast";
import { useHomepageImage, useHomepageImageToast } from "./hook";

export const HomePageImage = () => {
  const { toast, showToast, hideToast } = useHomepageImageToast();
  
  const {
    file,
    setFile,
    preview,
    currentImage,
    loading,
    error,
    handleUpdate,
  } = useHomepageImage();

  const handleUpdateClick = () => {
    handleUpdate(showToast);
  };

  return (
    <MainTemplate title="Homepage Image" useFitHeight>
      <StyledWrapper>
        <StyledUploadRow>
          <ImageUpload
            name="homepageImage"
            value={file}
            onChange={setFile}
            fullWidth={false}
          />
        </StyledUploadRow>
        <StyledLargePreviewWrapper>
          {loading && <StyledLoadingText>Loading...</StyledLoadingText>}
          {error && <StyledErrorText>{error}</StyledErrorText>}
          {!loading && !error && currentImage && (
            <StyledLargePreview src={currentImage} alt="Current Homepage Cover" />
          )}
          {!loading && !error && !currentImage && (
            <StyledNoImageText>No homepage cover image found</StyledNoImageText>
          )}
        </StyledLargePreviewWrapper>
        <StyledButtonRow>
          <StyledUpdateButton
            onClick={handleUpdateClick}
            disabled={!file || loading}
          >
            {loading ? 'Updating...' : 'Update'}
          </StyledUpdateButton>
        </StyledButtonRow>
      </StyledWrapper>
      {toast.visible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </MainTemplate>
  );
};

const StyledWrapper = styled.div`
  margin-top: 8px;
  width: 100%;

  @media (max-width: 768px) {
    margin-top: 4px;
  }

  @media (max-width: 480px) {
    margin-top: 2px;
  }
`;

const StyledUploadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    gap: 8px;
    margin-bottom: 16px;
  }
`;

const StyledThumb = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  img {
    width: 64px;
    height: 64px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #ccc;
  }
  span {
    font-size: 15px;
    margin-top: 2px;
  }

  @media (max-width: 768px) {
    img {
      width: 56px;
      height: 56px;
    }
    span {
      font-size: 14px;
    }
  }

  @media (max-width: 480px) {
    img {
      width: 48px;
      height: 48px;
    }
    span {
      font-size: 13px;
    }
  }
`;

const StyledLargePreviewWrapper = styled.div`
  margin: 0 auto 32px auto;
  width: 100%;
  min-height: 450px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e0e4ea;

  @media (max-width: 768px) {
    min-height: 350px;
    margin-bottom: 24px;
  }

  @media (max-width: 480px) {
    min-height: 250px;
    margin-bottom: 20px;
    border-radius: 6px;
  }
`;

const StyledLargePreview = styled.img`
  max-width: 100%;
  max-height: 400px;
  border-radius: 8px;
  object-fit: contain;

  @media (max-width: 768px) {
    max-height: 300px;
    border-radius: 6px;
  }

  @media (max-width: 480px) {
    max-height: 200px;
    border-radius: 4px;
  }
`;

const StyledButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;

  @media (max-width: 768px) {
    justify-content: center;
    margin-top: 20px;
  }

  @media (max-width: 480px) {
    margin-top: 16px;
  }
`;

const StyledUpdateButton = styled.button`
  background: #4caf50;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 32px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover:not(:disabled) {
    background: #388e3c;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 10px 24px;
    font-size: 15px;
    border-radius: 6px;
  }

  @media (max-width: 480px) {
    padding: 8px 20px;
    font-size: 14px;
    border-radius: 4px;
    width: 100%;
    max-width: 200px;
  }
`;

const StyledLoadingText = styled.div`
  color: #666;
  font-size: 16px;

  @media (max-width: 768px) {
    font-size: 15px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const StyledErrorText = styled.div`
  color: #d32f2f;
  font-size: 16px;

  @media (max-width: 768px) {
    font-size: 15px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const StyledNoImageText = styled.div`
  color: #666;
  font-size: 16px;

  @media (max-width: 768px) {
    font-size: 15px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;
